import React, { createContext, useContext, useState, useEffect } from 'react';
import { portfolioApi } from '../services/api';

const CACHE_KEY = 'portfolio_data_v1';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

const defaultData = {
  personalInfo: {
    name: "Umashankar K H",
    title: "",
    tagline: "",
    email: "",
    phone: "",
    location: "",
    linkedin: "",
    github: "",
    summary: ""
  },
  skills: {
    technical: [],
    categories: []
  },
  experience: [],
  projects: [],
  education: [],
  certifications: [],
  settings: {
    site_title: "Portfolio",
    meta_description: "",
    primary_color: "#2563eb",
    secondary_color: "#1e3a5f"
  }
};

const readCache = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL) return null;
    return data;
  } catch {
    return null;
  }
};

const writeCache = (data) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ data, ts: Date.now() }));
  } catch {}
};

const PortfolioContext = createContext(defaultData);

export const usePortfolio = () => {
  const context = useContext(PortfolioContext);
  if (!context) {
    throw new Error('usePortfolio must be used within a PortfolioProvider');
  }
  return context;
};

export const PortfolioProvider = ({ children }) => {
  const cached = readCache();
  const [data, setData] = useState(cached || defaultData);
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    try {
      setError(null);
      const response = await portfolioApi.getAll();
      const newData = {
        personalInfo: response.personalInfo || defaultData.personalInfo,
        skills: response.skills || defaultData.skills,
        experience: response.experience || [],
        projects: response.projects || [],
        education: response.education || [],
        certifications: response.certifications || [],
        settings: response.settings || defaultData.settings
      };
      setData(newData);
      writeCache(newData);
    } catch (err) {
      console.error('Failed to fetch portfolio data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const refresh = () => {
    fetchData();
  };

  return (
    <PortfolioContext.Provider value={{ ...data, loading, error, refresh }}>
      {children}
    </PortfolioContext.Provider>
  );
};

export default PortfolioContext;
